import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Flame } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DealCard from "../components/DealCard";
import AdSlot from "../components/AdSlot";
import Seo, { JsonLd, schemas } from "../components/Seo";
import { api } from "../lib/api";

/**
 * Rich SEO copy for each LEGO category. Used as the page intro and meta description.
 * Helps Google understand what each category page is about and rank for category keywords.
 */
const CATEGORY_COPY = {
  "star-wars": {
    title: "Offerte LEGO Star Wars",
    intro:
      "Le migliori offerte sui set di costruzioni Star Wars: dalle astronavi più iconiche come il Millennium Falcon, agli X-Wing, ai diorami della saga. Aggiornato ogni giorno con sconti su Amazon, eBay e AliExpress.",
  },
  technic: {
    title: "Offerte LEGO Technic",
    intro:
      "Trovi qui le offerte sui set Technic: supercar Lamborghini, Ferrari, Porsche, McLaren e moto Ducati, oltre a macchine da cantiere e gru. Sconti curati ogni giorno.",
  },
  icons: {
    title: "Offerte LEGO Icons",
    intro:
      "La gamma Icons (ex Creator Expert) per adulti collezionisti: edifici iconici, modelli avanzati e set da esposizione. Confronta i prezzi tra Amazon, eBay e AliExpress.",
  },
  creator: {
    title: "Offerte LEGO Creator 3-in-1",
    intro:
      "I set Creator 3-in-1: tre costruzioni in una sola scatola, perfetti per stimolare la creatività. Confronta le migliori offerte aggiornate quotidianamente.",
  },
  city: {
    title: "Offerte LEGO City",
    intro:
      "Polizia, vigili del fuoco, centrale spaziale, cantieri: tutti i set della linea City scontati. Aggiornamenti quotidiani sulle offerte migliori del web.",
  },
  architecture: {
    title: "Offerte LEGO Architecture",
    intro:
      "Architecture porta nelle tue mani le città e i monumenti del mondo: Big Ben, Tour Eiffel, Statua della Libertà, Empire State Building. Sconti curati ogni giorno.",
  },
  "harry-potter": {
    title: "Offerte LEGO Harry Potter",
    intro:
      "Hogwarts, Diagon Alley, Hogwarts Express e tutti i set della saga di Harry Potter. Ogni giorno selezioniamo le offerte più convenienti per te.",
  },
  marvel: {
    title: "Offerte LEGO Marvel Super Heroes",
    intro:
      "Avengers, Spider-Man, Iron Man, Thor: tutti i set Marvel scontati su Amazon, eBay e AliExpress. Aggiornati quotidianamente.",
  },
  dc: {
    title: "Offerte LEGO DC Super Heroes",
    intro:
      "Batman, Superman, Wonder Woman, Joker: i migliori set DC scontati. Sconti aggiornati ogni giorno.",
  },
  ninjago: {
    title: "Offerte LEGO Ninjago",
    intro:
      "I ninja di Ninjago, le loro spedizioni, i Mech e i veicoli. Tutte le migliori offerte aggiornate ogni giorno.",
  },
  friends: {
    title: "Offerte LEGO Friends",
    intro:
      "La linea Friends: locali di Heartlake City, case sull'albero, beach club. Sconti quotidiani sulle migliori offerte online.",
  },
  "speed-champions": {
    title: "Offerte LEGO Speed Champions",
    intro:
      "Le auto da corsa più famose in scala mini: Ferrari, McLaren, Porsche, Lamborghini, Mercedes-AMG. Sconti aggiornati ogni giorno.",
  },
  disney: {
    title: "Offerte LEGO Disney",
    intro:
      "I set ispirati ai film e personaggi Disney: castelli, principesse, classici animati. Le offerte migliori del web in un colpo solo.",
  },
  ideas: {
    title: "Offerte LEGO Ideas",
    intro:
      "Set unici nati dalla community LEGO: dai cult cinematografici alle icone pop. Disponibilità limitata, sconti da non perdere.",
  },
  minecraft: {
    title: "Offerte LEGO Minecraft",
    intro:
      "Tutti i set ufficiali LEGO Minecraft: villaggi, biomi, personaggi del gioco. Selezione quotidiana di offerte.",
  },
  "super-mario": {
    title: "Offerte LEGO Super Mario",
    intro:
      "L'universo Super Mario in versione LEGO: starter pack, espansioni e personaggi interattivi. Aggiornato ogni giorno.",
  },
  "animal-crossing": {
    title: "Offerte LEGO Animal Crossing",
    intro:
      "I set della linea Animal Crossing: case, animaletti, ambientazioni dolci dell'isola. Sconti aggiornati ogni giorno.",
  },
  botanical: {
    title: "Offerte LEGO Botanicals",
    intro:
      "Fiori, bouquet, bonsai, orchidee: la collezione Botanicals perfetta come regalo e decorazione. Ogni giorno aggiorniamo le offerte.",
  },
  dreamzzz: {
    title: "Offerte LEGO DREAMZzz",
    intro:
      "I set della linea DREAMZzz: avventure oniriche, mech trasformabili e personaggi della serie. Sconti quotidiani.",
  },
  duplo: {
    title: "Offerte LEGO DUPLO",
    intro:
      "La gamma DUPLO per i più piccoli (1-5 anni): mattoncini grandi, sicuri e colorati. Trovi qui tutte le migliori offerte aggiornate.",
  },
  brickheadz: {
    title: "Offerte LEGO BrickHeadz",
    intro:
      "I personaggi BrickHeadz da collezione: dimensioni compatte, dettagli precisi, prezzi accessibili. Sconti aggiornati ogni giorno.",
  },
  sonic: {
    title: "Offerte LEGO Sonic the Hedgehog",
    intro:
      "I set ufficiali del riccio blu: Green Hill Zone, Death Egg Robot, personaggi e veicoli. Aggiornamento giornaliero delle offerte.",
  },
  avatar: {
    title: "Offerte LEGO Avatar",
    intro:
      "I set dei film Avatar di James Cameron: paesaggi di Pandora, creature, Na'vi. Confronto prezzi aggiornato ogni giorno.",
  },
  wicked: {
    title: "Offerte LEGO Wicked",
    intro:
      "La nuovissima linea LEGO Wicked tratta dal musical: Emerald City, personaggi, ambientazioni magiche. Tutte le offerte in un colpo solo.",
  },
  "one-piece": {
    title: "Offerte LEGO One Piece",
    intro:
      "I set ufficiali One Piece: la Going Merry, Thousand Sunny, ciurma di Cappello di Paglia. Sconti aggiornati ogni giorno.",
  },
  minifigures: {
    title: "Offerte LEGO Minifigures",
    intro:
      "Bustine collezionabili Minifigures: tutte le serie scontate. Quando le trovi al miglior prezzo le segnaliamo subito qui.",
  },
  altro: {
    title: "Altre offerte LEGO",
    intro:
      "Tutte le offerte che non rientrano in una linea specifica: regali, gadget, mattoncini sciolti, accessori. Aggiornate ogni giorno.",
  },
};

const CATEGORY_FAQ = {
  "star-wars": [
    { q: "Quanto costano i set LEGO Star Wars?", a: "I prezzi vanno dai polybag a ~€10 fino agli UCS (Ultimate Collector Series) oltre €800. La fascia più popolare è €40-€200." },
    { q: "Quali set Star Wars sono più ambiti?", a: "Le serie UCS, Master Builder e Diorama Collection. Il Millennium Falcon UCS e l'X-Wing sono tra i più richiesti." },
    { q: "Dove conviene comprare LEGO Star Wars scontati?", a: "Amazon e LEGO Shop offrono le promozioni più frequenti. eBay e AliExpress sono interessanti per set fuori produzione." },
  ],
  technic: [
    { q: "I LEGO Technic sono adatti ai bambini?", a: "I Technic sono pensati per costruttori esperti (età 9+). Per i più piccoli meglio Creator o DUPLO." },
    { q: "Quali set Technic hanno il motore?", a: "Le supercar di punta come Bugatti Chiron, Lamborghini Sián e Ferrari Daytona SP3 hanno motori e meccanismi funzionali." },
    { q: "I LEGO Technic si rivalutano nel tempo?", a: "Sì, soprattutto i flagship in edizione limitata. La Bugatti Chiron è cresciuta del 30-100% dopo il ritiro." },
  ],
  icons: [
    { q: "Cos'è la linea LEGO Icons?", a: "Icons è la gamma per adulti (18+), evoluzione di Creator Expert. Architettura, veicoli vintage, edifici modulari." },
    { q: "Quali sono i LEGO Icons più scontati?", a: "Botanical (orchidee, bouquet) ed edifici modulari hanno sconti ricorrenti del 20-30% su Amazon nei periodi promozionali." },
  ],
  city: [
    { q: "I LEGO City sono compatibili tra loro?", a: "Sì, tutti i set LEGO City sono compatibili e si possono combinare per creare una vera città." },
    { q: "Quanti set City escono ogni anno?", a: "LEGO rilascia 40-60 set City all'anno, divisi in Polizia, Vigili del Fuoco, Spazio, Foresta, Cantiere." },
  ],
  "harry-potter": [
    { q: "Quali sono i set Harry Potter più ambiti?", a: "Hogwarts Castle (71043) e Diagon Alley sono i più amati dai collezionisti." },
    { q: "Le minifigure Harry Potter sono esclusive dei set?", a: "Sì, molte minifigure sono disponibili SOLO in specifici set e diventano rare dopo il ritiro." },
  ],
};

const DEFAULT_FAQ = (cat) => [
  { q: `Quali offerte trovo in questa categoria?`, a: `Aggreghiamo ogni giorno le migliori offerte da Amazon, eBay e AliExpress per i set ${cat}.` },
  { q: `Come funziona BrickDailyDeals?`, a: `Iscrivendoti al canale Telegram @BrickDailyDeals ricevi notifiche istantanee su tutti i nuovi sconti.` },
  { q: `Le offerte sono affidabili?`, a: `Sì, i link rimandano al venditore ufficiale. Verifica sempre il prezzo al momento dell'acquisto.` },
];

export default function CategoryPage() {
  const { key } = useParams();

  // Hydrate from prerender bootstrap when present
  const bootstrap = (typeof window !== "undefined" && window.__BDD_INITIAL__) || null;
  const initialCats = bootstrap?.categories?.length ? bootstrap.categories : undefined;
  const initialCatDeals = bootstrap?.route === "category"
    ? (bootstrap.deals || []).filter((d) => d.category === key)
    : (bootstrap?.deals || []).filter((d) => d.category === key);

  // Validate category
  const { data: cats, isLoading: catsLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get("/categories").then((r) => r.data),
    staleTime: 60 * 60_000,
    initialData: initialCats,
  });

  const { data: dealsData, isLoading } = useQuery({
    queryKey: ["category-deals", key],
    queryFn: () =>
      api
        .get("/deals", { params: { category: key, limit: 60 } })
        .then((r) => r.data.items),
    initialData: initialCatDeals && initialCatDeals.length ? initialCatDeals : undefined,
  });

  const deals = dealsData || [];
  const cat = (cats || []).find((c) => c.key === key);
  const copy =
    CATEGORY_COPY[key] || {
      title: cat ? `Offerte LEGO ${cat.label}` : "Categoria",
      intro:
        "Tutte le migliori offerte di questa categoria, aggiornate ogni giorno dal canale BrickDailyDeals.",
    };

  // Long-form editorial intro fetched from the backend (P2 — AdSense content richness).
  const { data: catContent } = useQuery({
    queryKey: ["category-content", key],
    queryFn: () => api.get(`/categories/${key}/content`).then((r) => r.data),
    staleTime: 10 * 60_000,
  });
  const introLong = catContent?.intro_long || "";
  const highlights = Array.isArray(catContent?.highlights) ? catContent.highlights : [];

  // If the category is unknown after categories loaded, redirect home
  if (!catsLoading && cats && cats.length && !cat) {
    return <Navigate to="/" replace />;
  }

  const hotCount = deals.filter((d) => d.hot).length;
  const faq = CATEGORY_FAQ[key] || DEFAULT_FAQ(copy.title);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-[var(--bdd-bg)]" data-testid="page-category">
      <Seo
        title={copy.title}
        description={copy.intro.slice(0, 180)}
        path={`/categoria/${key}`}
      />
      <JsonLd
        data={schemas.breadcrumb([
          { name: "Home", url: "/" },
          { name: copy.title, url: `/categoria/${key}` },
        ])}
      />
      <JsonLd data={schemas.itemList(deals)} />
      <JsonLd data={faqSchema} />
      <Header />

      <section className="border-b border-zinc-900 bg-gradient-to-b from-[#0d162d]/60 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link
            to="/"
            data-testid="category-back"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6"
          >
            <ArrowLeft size={14} /> Tutte le categorie
          </Link>

          <div className="font-mono-bdd text-xs uppercase tracking-[0.3em] text-[#FBD51C] mb-3">
            Categoria
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4">
            {copy.title}
          </h1>
          <p className="text-zinc-400 text-lg max-w-3xl leading-relaxed">
            {copy.intro}
          </p>

          {highlights.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="category-highlights">
              {highlights.map((h, i) => (
                <span
                  key={i}
                  className="text-[11px] font-mono-bdd uppercase tracking-wider px-3 py-1 rounded-full bg-[#FBD51C]/10 text-[#FBD51C] border border-[#FBD51C]/25"
                >
                  {h}
                </span>
              ))}
            </div>
          )}

          {introLong ? (
            <div
              className="mt-8 max-w-3xl space-y-4 text-zinc-300 text-sm leading-relaxed"
              data-testid="category-intro-long"
            >
              {introLong.split(/\n{2,}/).map((para, i) => (
                <p key={i}>{para.trim()}</p>
              ))}
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
            <span className="text-zinc-500">
              <strong className="text-white">{deals.length}</strong> offerte attive
            </span>
            {hotCount > 0 && (
              <span className="inline-flex items-center gap-1.5 text-rose-300">
                <Flame size={14} className="text-rose-400" />
                <strong>{hotCount}</strong> HOT
              </span>
            )}
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="aspect-[4/3] rounded-2xl shimmer"
              />
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-zinc-400 text-lg mb-2">
              Nessuna offerta attiva al momento per questa categoria.
            </div>
            <div className="text-sm text-zinc-500">
              Iscriviti al canale Telegram per essere il primo a sapere quando arrivano nuove offerte.
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 bg-[#FBD51C] text-black font-bold px-5 py-3 rounded-full"
            >
              Vedi tutte le offerte
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {deals.map((d, i) => (
              <React.Fragment key={d.id}>
                <DealCard deal={d} />
                {i === 7 && (
                  <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
                    <AdSlot id={`category-${key}-mid`} size="leaderboard" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </main>

      <section className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="font-mono-bdd text-xs uppercase tracking-[0.3em] text-[#FBD51C] mb-3">
            FAQ
          </div>
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-8">
            Domande frequenti su {copy.title}
          </h2>
          <div className="space-y-4">
            {faq.map((f, i) => (
              <details
                key={i}
                className="group bg-zinc-900 border border-zinc-800 rounded-xl p-5 open:border-[#FBD51C]/40 transition-colors"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-3 font-bold text-white">
                  {f.q}
                  <span className="text-[#FBD51C] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-zinc-400 leading-relaxed text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
